import { NetworkError } from "@pandas/http-client";
import { servicesOrchestratorValues } from "@/config/services";
import { useStore } from "@/store";

type NetworkStatus = "online" | "offline" | "recovered";

class NetworkManager {
	private static instance: NetworkManager;
	private status: NetworkStatus = navigator.onLine ? "online" : "offline";
	private requestQueue: Array<() => Promise<unknown>> = [];
	private modalTimeout?: ReturnType<typeof setTimeout>;
	private wasOffline = false;
	private recoveryHandled = false;
	private requestDuringOffline = false;
	private offlineModalShown = false;
	private readonly timeoutSeconds = servicesOrchestratorValues.app.networkManager.timeoutSeconds;
	private readonly isEnabled = servicesOrchestratorValues.app.networkManager.enabled;

	private constructor() {
		if (!this.isEnabled) return;

		window.addEventListener("online", this.handleConnectionChange);
		window.addEventListener("offline", this.handleConnectionChange);
	}

	public static getInstance(): NetworkManager {
		if (!NetworkManager.instance) {
			NetworkManager.instance = new NetworkManager();
		}
		return NetworkManager.instance;
	}

	public getRequestQueueLength() {
		return this.requestQueue.length;
	}

	public queueRequest(requestFn: () => Promise<unknown>) {
		if (!this.isEnabled) {
			requestFn();
			return;
		}

		this.requestQueue.push(requestFn);

		if (this.status === "offline" && !this.modalTimeout && !this.recoveryHandled) {
			this.requestDuringOffline = true;

			this.modalTimeout = setTimeout(() => {
				if (!navigator.onLine && this.status === "offline") {
					this.offlineModalShown = true;
					useStore.getState().setError(new NetworkError());
				}
			}, this.timeoutSeconds * 1000);
		}

		if (this.status === "online") {
			useStore.getState().setError(new NetworkError("FETCH_FAILED_WHILE_ONLINE"));
		}
	}

	public runWhenOnline(fn: () => void): void {
		if (!this.isEnabled || this.status === "online") {
			fn();
			return;
		}

		this.queueRequest(() => Promise.resolve(fn()));
	}

	public retryQueue() {
		if (!this.isEnabled) return;

		const requestsToRetry = [...this.requestQueue];
		this.requestQueue = [];
		requestsToRetry.forEach((request) => request());
		this.status = "online";
	}
	public async retryQueueAsync() {
		if (!this.isEnabled) return;

		const requestsToRetry = [...this.requestQueue];
		this.requestQueue = [];

		for (const request of requestsToRetry) {
			await request();
		}
		this.status = "online";
	}
	public reset() {
		if (!this.isEnabled) return;

		this.requestQueue = [];
		this.status = navigator.onLine ? "online" : "offline";
		this.wasOffline = false;
		this.recoveryHandled = false;
		this.requestDuringOffline = false;
		this.offlineModalShown = false;
		clearTimeout(this.modalTimeout);
		this.modalTimeout = undefined;
	}

	public getStatus() {
		return {
			isOnline: this.isEnabled ? this.status === "online" : true,
		};
	}

	private handleConnectionChange = () => {
		if (!this.isEnabled) return;

		if (navigator.onLine) {
			this.handleOnline();
		} else {
			this.handleOffline();
		}
	};

	private handleOnline() {
		if (!this.isEnabled) return;

		clearTimeout(this.modalTimeout);
		this.modalTimeout = undefined;

		if (this.wasOffline && !this.recoveryHandled && this.requestDuringOffline && this.offlineModalShown) {
			this.status = "recovered";
			this.wasOffline = false;
			this.recoveryHandled = true;
			useStore.getState().setError(new NetworkError("IS_BACK_ONLINE"));
		} else {
			this.status = "online";

			if (this.requestQueue.length > 0) {
				const requestsToRetry = [...this.requestQueue];
				this.requestQueue = [];
				requestsToRetry.forEach((request) => request());
			}
		}

		this.requestDuringOffline = false;
		this.offlineModalShown = false;
	}

	private handleOffline() {
		if (!this.isEnabled) return;

		this.wasOffline = true;
		this.recoveryHandled = false;
		this.status = "offline";
	}
}

export const networkManager = NetworkManager.getInstance();
